// ============================================================================
// Audit v2 — Référentiels (Phase A, from scratch)
//
// Catalogues passifs : { code, label, icon, color }. Aucune table métier ne
// pointe vers ces référentiels par FK. C'est juste un dictionnaire de
// résolution au moment du rendu de la grille syno.
//
// Les codes stables vivent en BD (audit_flux_lines, colonnes *_code). Si un label
// change ici, les anciens audits affichent automatiquement le nouveau label.
// Si on supprime un code, les lignes existantes affichent le code brut
// (lookup → null → fallback). Pas de migration de données nécessaire.
//
// Storage : constantes en code (option (a) du doc). Bascule vers tables DB
// envisageable plus tard si besoin de personnalisation per-tenant.
// Réf. doc : docs/audit-v2-architecture-3-couches.md
// ============================================================================

import {
  Box, Drum, Container, Package, Layers, ShoppingBasket, Trash2,
  Hand, Forklift, Wrench, Building2,
  Leaf, AlertTriangle, Cog, Scissors, Flame,
  Truck, Bike, ShieldAlert, Droplets,
  Recycle, RefreshCw, ArrowDownCircle, HelpCircle, Skull,
  type LucideIcon,
} from 'lucide-react'
import { MACRO_COLORS as DS_MACRO_COLORS } from '@cinqo/ui'

// ─── Step keys ──────────────────────────────────────────────────────────────

export const STEP_KEYS = [
  'pre_collecte',
  'collecte',
  'pretraitement',
  'conditionnement',
  'expedition',
  'prestataire',
  'filiere',
] as const

export type StepKey = typeof STEP_KEYS[number]

export const STEP_LABELS: Record<StepKey, string> = {
  pre_collecte:    'Pré-collecte',
  collecte:        'Collecte',
  pretraitement:   'Pré-traitement',
  conditionnement: 'Conditionnement',
  expedition:      'Expédition',
  prestataire:     'Prestataire',
  filiere:         'Filière',
}

// ─── Type d'entrée référentiel ─────────────────────────────────────────────

export interface RefEntry {
  code: string
  label: string
  icon: LucideIcon
  color?: string
  macro?: string | null
}

// ─── Macros (DIB / AMV / DID) ───────────────────────────────────────────────

export type Macro = 'DIB' | 'AMV' | 'DID'

export const MACROS: { code: Macro; label: string; color: string }[] = [
  { code: 'DIB', label: 'DIB — Banal',                   color: DS_MACRO_COLORS.DIB.colorHex },
  { code: 'AMV', label: 'AMV — Matières valorisables',   color: DS_MACRO_COLORS.AMV.colorHex },
  { code: 'DID', label: 'DID — Dangereux',               color: DS_MACRO_COLORS.DID.colorHex },
]

/**
 * @deprecated Utiliser MACRO_COLORS de @cinqo/ui (source unique).
 * Conserve ici pour compat avec les ~5 consumers historiques. Lecture uniquement.
 */
export const MACRO_COLORS: Record<Macro, string> = {
  DIB: DS_MACRO_COLORS.DIB.colorHex,
  AMV: DS_MACRO_COLORS.AMV.colorHex,
  DID: DS_MACRO_COLORS.DID.colorHex,
}

// ─── Pré-collecte (où la matière s'accumule avant d'être déplacée) ──────────

export const REF_PRE_COLLECTE: RefEntry[] = [
  { code: 'VRAC', label: 'Vrac',           icon: Layers },
  { code: 'FUT',  label: 'Fût',            icon: Drum },
  { code: 'BEN',  label: 'Benne',          icon: Container },
  { code: 'BBG',  label: 'Big Bag',        icon: ShoppingBasket },
  { code: 'BAL',  label: 'Balle',          icon: Package },
  { code: 'PAL',  label: 'Palette',        icon: Box },
  { code: 'B660', label: 'Bac 660L',       icon: Trash2 },
  { code: 'B240', label: 'Bac 240L',       icon: Trash2 },
  { code: 'AUT',  label: 'Autre',          icon: HelpCircle },
]

// ─── Collecte (déplacement interne sur le site) ─────────────────────────────

export const REF_COLLECTE: RefEntry[] = [
  { code: 'MAN', label: 'Manuelle',                 icon: Hand },
  { code: 'TRP', label: 'Transpalette',             icon: Forklift },
  { code: 'CHA', label: 'Chariot élévateur',        icon: Forklift },
  { code: 'MEN', label: 'Société de ménage',        icon: Building2 },
  { code: 'AUT', label: 'Autre',                    icon: HelpCircle },
]

// ─── Pré-traitement (transformation avant expédition) ──────────────────────

export const REF_PRETRAITEMENT: RefEntry[] = [
  { code: 'AUC', label: 'Aucun',                    icon: HelpCircle },
  { code: 'PBA', label: 'Presse à balle',           icon: Wrench },
  { code: 'COM', label: 'Compacteur',               icon: Cog },
  { code: 'BRO', label: 'Broyeur',                  icon: Scissors },
  { code: 'AUT', label: 'Autre',                    icon: HelpCircle },
]

// ─── Conditionnement (packaging final pour expédition) ─────────────────────

export const REF_CONDITIONNEMENT: RefEntry[] = [
  { code: 'VRAC', label: 'Vrac',          icon: Layers },
  { code: 'FUT',  label: 'Fût',           icon: Drum },
  { code: 'BEN',  label: 'Benne',         icon: Container },
  { code: 'BBG',  label: 'Big Bag',       icon: ShoppingBasket },
  { code: 'BAL',  label: 'Balle',         icon: Package },
  { code: 'PAL',  label: 'Palette',       icon: Box },
  { code: 'AUT',  label: 'Autre',         icon: HelpCircle },
]

// ─── Expédition (mode de sortie du site) ────────────────────────────────────

export const REF_EXPEDITION: RefEntry[] = [
  { code: 'AMP', label: 'Camion ampliroll',     icon: Truck },
  { code: 'BOM', label: 'Camion BOM',           icon: Truck },
  { code: 'POR', label: 'Camion porteur',       icon: Truck },
  { code: 'VL',  label: 'Véhicule léger',       icon: Bike },
  { code: 'ADR', label: 'Véhicule ADR',         icon: ShieldAlert },
  { code: 'CIT', label: 'Citerne',              icon: Droplets },
  { code: 'AUT', label: 'Autre',                icon: HelpCircle },
]

// ─── Filières (destination finale) ──────────────────────────────────────────

export const REF_FILIERES: RefEntry[] = [
  { code: 'VMA', label: 'Valorisation matière',     icon: Recycle,         color: '#22c55e' },
  { code: 'VEN', label: 'Valorisation énergétique', icon: Flame,           color: '#f59e0b' },
  { code: 'REM', label: 'Réemploi',                 icon: RefreshCw,       color: '#3b82f6' },
  { code: 'ENF', label: 'Enfouissement',            icon: ArrowDownCircle, color: '#ef4444' },
  { code: 'ISD', label: 'ISDD (dangereux)',         icon: Skull,           color: '#dc2626' },
  { code: 'INC', label: 'Inconnu',                  icon: HelpCircle,      color: '#9ca3af' },
]

// ─── Matières (option A : top 30 MVP, à enrichir avec CED plus tard) ────────

export const REF_MATIERES: RefEntry[] = [
  { code: 'CART', label: 'Carton',                icon: Package, color: '#22c55e' },
  { code: 'PAP',  label: 'Papier',                icon: Package, color: '#22c55e' },
  { code: 'PLAS', label: 'Plastique',             icon: Package, color: '#22c55e' },
  { code: 'FILM', label: 'Film plastique',        icon: Package, color: '#22c55e' },
  { code: 'BOIS', label: 'Bois / palettes',       icon: Box,     color: '#22c55e' },
  { code: 'VER',  label: 'Verre',                 icon: Box,     color: '#22c55e' },
  { code: 'MEFE', label: 'Métaux ferreux',        icon: Box,     color: '#22c55e' },
  { code: 'MENF', label: 'Métaux non ferreux',    icon: Box,     color: '#22c55e' },
  { code: 'DIB',  label: 'DIB en mélange',        icon: Trash2,  color: '#9ca3af' },
  { code: 'BIO',  label: 'Biodéchets',            icon: Leaf,    color: '#22c55e' },
  { code: 'TEX',  label: 'Textiles',              icon: Package, color: '#22c55e' },
  { code: 'GRA',  label: 'Gravats / inertes',     icon: Box,     color: '#9ca3af' },
  { code: 'PLAT', label: 'Plâtre',                icon: Box,     color: '#9ca3af' },
  { code: 'IBC',  label: 'IBC / contenants vides',icon: Container, color: '#3b82f6' },
  // ─── DEEE et assimilés ─────────────────────────────────────────────────
  { code: 'DEEE', label: 'DEEE',                  icon: Cog,           color: '#3b82f6' },
  { code: 'PILE', label: 'Piles',                 icon: AlertTriangle, color: '#3b82f6' },
  { code: 'BAT',  label: 'Batteries',             icon: AlertTriangle, color: '#3b82f6' },
  { code: 'CART2',label: 'Cartouches encre',      icon: Package,       color: '#3b82f6' },
  { code: 'LAMP', label: 'Lampes / néons',        icon: AlertTriangle, color: '#3b82f6' },
  // ─── Dangereux ─────────────────────────────────────────────────────────
  { code: 'HUI',  label: 'Huiles usagées',        icon: Droplets,      color: '#ef4444' },
  { code: 'SOLV', label: 'Solvants',              icon: Droplets,      color: '#ef4444' },
  { code: 'PEIN', label: 'Peintures',             icon: Droplets,      color: '#ef4444' },
  { code: 'ACID', label: 'Acides / bases',        icon: AlertTriangle, color: '#ef4444' },
  { code: 'AERO', label: 'Aérosols',              icon: AlertTriangle, color: '#ef4444' },
  { code: 'AMI',  label: 'Amiante',               icon: Skull,         color: '#ef4444' },
  { code: 'DASRI',label: 'DASRI (médical)',       icon: Skull,         color: '#ef4444' },
  { code: 'PHAR', label: 'Médicaments',           icon: AlertTriangle, color: '#ef4444' },
  // ─── Liquides et résidus ───────────────────────────────────────────────
  { code: 'EAUX', label: 'Eaux souillées',        icon: Droplets,      color: '#ef4444' },
  { code: 'BOUE', label: 'Boues',                 icon: Droplets,      color: '#ef4444' },
  { code: 'GRAI', label: 'Graisses alimentaires', icon: Droplets,      color: '#22c55e' },
  // ─── Autre ─────────────────────────────────────────────────────────────
  { code: 'AUT',  label: 'Autre',                 icon: HelpCircle,    color: '#9ca3af' },
]

// ─── Mapping step → référentiel ─────────────────────────────────────────────

export const STEP_REFS: Record<StepKey, RefEntry[]> = {
  pre_collecte:    REF_PRE_COLLECTE,
  collecte:        REF_COLLECTE,
  pretraitement:   REF_PRETRAITEMENT,
  conditionnement: REF_CONDITIONNEMENT,
  expedition:      REF_EXPEDITION,
  prestataire:     [], // dynamique per-client, free text + autocomplete sur usages passés
  filiere:         REF_FILIERES,
}

// ─── Helpers de résolution ──────────────────────────────────────────────────

/** Résout un code en RefEntry pour une step donnée. Null si introuvable. */
export function resolveRef(step: StepKey, code: string | null | undefined): RefEntry | null {
  if (!code) return null
  const ref = STEP_REFS[step]
  return ref.find(e => e.code === code) ?? null
}
